// src/pages/FriendshipVibeDashboard.jsx
//
// The dashboard. Until October 2026 it was built around a game: XP and a
// ten-level ladder in the header, a streak calendar, daily goals, level
// badges, "Module Achievements" trophies, certificates and "+10 XP" for every
// insight you saved. All of that went (docs/REVIEW_2026-10.md, Decision 1).
//
// What it shows now:
//   Today        - pick up where you left off; one thing to try this week
//                  (from a module you've finished); the tip of the day; your
//                  Friend Circle; your Vibe Check.
//   Your record  - a plain record of what you've done: modules finished,
//                  whether you took the Vibe Check, insights saved.
//   Insights     - the insights you've saved.
//
// Australian English throughout.
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/shared/Tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/shared/CardComponents';
import Button from '../components/shared/Button';
import {
  Brain, Users, ArrowRight, BookOpen, Wrench, Target,
  Download, Eye, Trash2, Lightbulb, Sparkles, ClipboardList,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useProgressStore } from '../stores/progressStore';
import { usePrivacyAuth } from '../context/PrivacyAuthContext';
import { useCirclesStore } from '../stores/circlesStore';
import { friendshipArchetypes } from '../data/friendshipAssessmentData';
import TipOfTheDay from '../components/dashboard/TipOfTheDay';
import PickUpWhereYouLeftOff from '../components/dashboard/PickUpWhereYouLeftOff';
import TryThisWeek from '../components/dashboard/TryThisWeek';
import WhatYouveDone from '../components/dashboard/WhatYouveDone';
import DonutChart from '../components/shared/DonutChart';

const FriendshipVibeDashboard = () => {
  // What you've done: a plain record (stores/progressStore.js).
  const completedModules = useProgressStore((state) => state.completedModules);
  const moduleCompletedAt = useProgressStore((state) => state.moduleCompletedAt);
  const savedInsightsCount = useProgressStore((state) => state.savedInsights.length);

  const { setCurrentView, assessmentResults } = useApp();
  const { user } = usePrivacyAuth();
  const { myCircles, activeCircleId } = useCirclesStore();

  const [activeTab, setActiveTab] = useState('today');

  const hasAssessment = useMemo(
    () => !!assessmentResults && Object.keys(assessmentResults).length > 0,
    [assessmentResults]
  );
  const hasProgress = (completedModules?.length || 0) > 0 || savedInsightsCount > 0;

  const resultsData = useMemo(
    () => assessmentResults?.insights || assessmentResults || {},
    [assessmentResults]
  );
  const { dominantArchetypes = [], archetypePercentages = {}, overallResult = {} } = resultsData;

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    const name = user?.username || 'Friend';
    if (hour < 12) return `Good morning, ${name}!`;
    if (hour < 17) return `Good afternoon, ${name}!`;
    return `Good evening, ${name}!`;
  }, [user]);

  // ====================
  // FIRST VISIT
  // ====================
  if (!hasAssessment && !hasProgress) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center min-h-[60vh] p-6 sm:p-8 pt-24 sm:pt-32"
      >
        <div className="text-center max-w-md w-full space-y-6">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-teal-500/20 to-coral-500/20 rounded-full flex items-center justify-center border border-teal-500/30">
            <Users className="w-10 h-10 text-teal-400" />
          </div>
          <h2 className="text-2xl font-bold text-white">Start Your Friendship Journey</h2>
          <p className="text-slate-400 leading-relaxed">
            Take the Friendship Vibe Check to see your friendship style, or go straight to the modules.
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => setCurrentView('vibe-assessment-intro')}
              className="w-full bg-gradient-to-r from-teal-600 to-coral-600 hover:from-teal-700 hover:to-coral-700 text-white font-semibold py-3 px-6 rounded-lg"
            >
              <Brain className="w-5 h-5 mr-2" />
              Take Friendship Vibe Check
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <div className="text-left">
              <PickUpWhereYouLeftOff completedModules={completedModules} setCurrentView={setCurrentView} />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // ====================
  // DASHBOARD
  // ====================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 pt-20 sm:pt-32">
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="mx-auto mb-6 max-w-sm">
            <img
              src="/images/friendship-dashboard.png"
              alt=""
              className="w-full h-auto rounded-2xl shadow-2xl shadow-teal-500/20"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {greeting}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-teal-300">
            {overallResult?.vibeCheck || 'Here’s where you are.'}
          </p>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="grid w-full grid-cols-3 bg-white/10 backdrop-blur-sm">
            <TabsTrigger value="today">Today</TabsTrigger>
            <TabsTrigger value="record">Your record</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          {/* TODAY */}
          <TabsContent value="today" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PickUpWhereYouLeftOff completedModules={completedModules} setCurrentView={setCurrentView} />
              <TryThisWeek completedModules={completedModules} moduleCompletedAt={moduleCompletedAt} />
            </div>

            <TipOfTheDay completedModules={completedModules} setCurrentView={setCurrentView} />

            <FriendCirclesWidget
              myCircles={myCircles}
              activeCircleId={activeCircleId}
              setCurrentView={setCurrentView}
            />

            {hasAssessment && (
              <VibeCheckCard dominantArchetypes={dominantArchetypes} setCurrentView={setCurrentView} />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <FriendshipDNA archetypePercentages={archetypePercentages} dominantArchetypes={dominantArchetypes} setCurrentView={setCurrentView} />
              <QuickActions setCurrentView={setCurrentView} hasAssessment={hasAssessment} />
            </div>
          </TabsContent>

          {/* YOUR RECORD */}
          <TabsContent value="record" className="space-y-6">
            <WhatYouveDone
              completedModules={completedModules}
              moduleCompletedAt={moduleCompletedAt}
              savedInsightsCount={savedInsightsCount}
              assessmentResults={assessmentResults}
              setCurrentView={setCurrentView}
              onShowInsights={() => setActiveTab('insights')}
            />
          </TabsContent>

          {/* INSIGHTS */}
          <TabsContent value="insights" className="space-y-6">
            <InsightsJournal />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ====================
// SUB-COMPONENTS
// ====================

const FriendCirclesWidget = React.memo(function FriendCirclesWidget({ myCircles, activeCircleId, setCurrentView }) {
  const activeCircle = myCircles.find(c => c.id === activeCircleId);

  // If user has no circles, show invitation to create/join
  if (!myCircles || myCircles.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30 border-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Users className="h-6 w-6 text-purple-400" />
            Friend Circles
            <motion.span
              className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full font-medium"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              New!
            </motion.span>
          </CardTitle>
          <CardDescription className="text-purple-300">
            The modules, with your friends
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="text-5xl mb-4">👥</div>
            <h3 className="text-lg font-bold text-white mb-2">
              Learn Friendship Skills Together
            </h3>
            <p className="text-purple-200 mb-4 text-sm">
              Do the modules with friends you already know: the same module, one question everyone answers at once, then talk about it in person.
            </p>
            <Button
              onClick={() => setCurrentView('friend-circles')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              <Users className="w-4 h-4 mr-2" />
              Learn More
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If user has circles, show active circle info
  return (
    <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-white">
              <Users className="h-5 w-5 text-purple-400" />
              Your Friend Circle
            </CardTitle>
            <CardDescription className="text-purple-300">
              What you&apos;re on, and this week&apos;s question
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentView('circle-dashboard')}
            className="border-purple-500/30 text-purple-300 hover:bg-purple-500/20"
          >
            View Circle
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {activeCircle ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 bg-white/10 rounded-lg">
              <div className="text-3xl">{activeCircle.emoji}</div>
              <div className="flex-1">
                <div className="font-bold text-white">{activeCircle.name}</div>
                <div className="text-sm text-purple-300">
                  {activeCircle.members.length} {activeCircle.members.length === 1 ? 'member' : 'members'}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button
                size="sm"
                onClick={() => setCurrentView('modules')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Learn Together
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentView('friend-circles')}
                className="border-purple-500/30 text-purple-300 hover:bg-purple-500/20"
              >
                <Eye className="w-4 h-4 mr-2" />
                Open circle
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-purple-200 mb-3 text-sm">
              You&apos;re in {myCircles.length} {myCircles.length === 1 ? 'circle' : 'circles'} but haven&apos;t set one as active.
            </p>
            <Button
              size="sm"
              onClick={() => setCurrentView('friend-circles')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Choose Active Circle
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

const InsightsJournal = React.memo(function InsightsJournal() {
  const savedInsights = useProgressStore((state) => state.savedInsights);
  const deleteInsight = useProgressStore((state) => state.deleteInsight);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');

  const filteredInsights = useMemo(() => {
    let filtered = savedInsights;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(insight =>
        insight.quote?.toLowerCase().includes(term) ||
        insight.personalNote?.toLowerCase().includes(term) ||
        insight.moduleName?.toLowerCase().includes(term)
      );
    }

    if (selectedTag !== 'all') {
      filtered = filtered.filter(insight => insight.tags?.includes(selectedTag));
    }

    return filtered;
  }, [savedInsights, searchTerm, selectedTag]);

  const allTags = useMemo(() => {
    const tags = new Set();
    savedInsights.forEach(insight => {
      insight.tags?.forEach(tag => tags.add(tag));
    });
    return Array.from(tags);
  }, [savedInsights]);

  // Export insights as markdown
  const exportInsights = () => {
    const markdown = filteredInsights.map(insight => {
      const tags = insight.tags?.join(', ') || '';
      const date = new Date(insight.timestamp).toLocaleDateString('en-AU');

      return `## ${insight.moduleName || 'Insight'} - ${date}\n\n` +
        `**Tags:** ${tags}\n\n` +
        (insight.quote ? `> ${insight.quote}\n\n` : '') +
        (insight.personalNote ? `**My Reflection:**\n${insight.personalNote}\n\n` : '') +
        `---\n\n`;
    }).join('');

    const blob = new Blob([`# My Friendship Insights\n\n${markdown}`], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'friendship-insights.md';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (savedInsights.length === 0) {
    return (
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardContent className="py-12">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/10 rounded-full flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-yellow-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Your Insight Journal</h3>
            <p className="text-slate-300 max-w-md mx-auto">
              When something in a module lands, use &quot;Save insight&quot; to keep the line, and what you thought
              about it. They&apos;ll be here, and only you can see them.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search insights..."
                aria-label="Search insights"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                aria-label="Filter by tag"
                className="min-w-0 flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              >
                <option value="all">All Tags</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
              <Button
                onClick={exportInsights}
                variant="outline"
                className="border-white/10 text-white hover:bg-white/10"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Insights List */}
      <div className="space-y-4">
        {filteredInsights.length === 0 ? (
          <Card className="bg-white/5 backdrop-blur-sm border-white/10">
            <CardContent className="py-8">
              <div className="text-center text-slate-400">
                No insights found matching your filters
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredInsights.map((insight) => (
            <motion.div
              key={insight.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-gradient-to-br from-purple-500/5 to-blue-500/5 border-purple-500/20 hover:border-purple-500/40 transition-colors">
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-white text-lg flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        {insight.moduleName || 'Insight'}
                      </CardTitle>
                      {insight.section && (
                        <CardDescription className="text-slate-400">
                          {insight.section}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-slate-500">
                        {new Date(insight.timestamp).toLocaleDateString('en-AU')}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteInsight(insight.id)}
                        aria-label="Delete this insight"
                        className="p-1 hover:bg-red-500/20 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {insight.quote && (
                    <div className="pl-4 border-l-2 border-yellow-400/50">
                      <p className="text-slate-300 italic">{insight.quote}</p>
                    </div>
                  )}
                  {insight.personalNote && (
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-sm text-slate-300">{insight.personalNote}</p>
                    </div>
                  )}
                  {insight.tags && insight.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {insight.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 text-xs bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
});

const FriendshipDNA = React.memo(function FriendshipDNA({ archetypePercentages, dominantArchetypes, setCurrentView }) {
  if (!archetypePercentages || Object.keys(archetypePercentages).length === 0) {
    return (
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Brain className="h-5 w-5 text-teal-400" />
            Your Friendship DNA
          </CardTitle>
          <CardDescription className="text-slate-300">
            Take the assessment to discover your unique friendship style
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-4xl mb-4">🧬</div>
            <p className="text-slate-400 mb-4">Your friendship DNA awaits discovery!</p>
            <Button
              onClick={() => setCurrentView('vibe-assessment-intro')}
              className="bg-gradient-to-r from-teal-600 to-coral-600 hover:from-teal-700 hover:to-coral-700"
            >
              Take Assessment
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare data for donut chart
  const chartData = Object.entries(archetypePercentages)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5) // Show top 5
    .map(([archetype, percentage], index) => {
      const archetypeData = friendshipArchetypes[archetype];
      const colors = ['teal', 'purple', 'blue', 'pink', 'green'];

      return {
        label: archetypeData?.name || archetype,
        value: percentage,
        color: colors[index],
        emoji: archetypeData?.emoji || '✨',
      };
    });

  const topArchetype = dominantArchetypes[0]?.details || friendshipArchetypes[Object.keys(archetypePercentages)[0]];

  return (
    <Card className="bg-gradient-to-br from-teal-500/10 to-purple-500/10 border-teal-500/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <Brain className="h-5 w-5 text-teal-400" />
          Your Friendship DNA
        </CardTitle>
        <CardDescription className="text-slate-300">
          Your unique blend of friendship styles
        </CardDescription>
      </CardHeader>
      <CardContent className="py-6">
        <DonutChart
          data={chartData}
          size={180}
          strokeWidth={35}
          centerContent={
            <div className="text-center">
              <div className="text-4xl mb-1">{topArchetype?.emoji || '✨'}</div>
              <div className="text-xs text-slate-400">Primary</div>
            </div>
          }
          showLegend={true}
        />

        {topArchetype?.superpower && (
          <div className="mt-6 pt-4 border-t border-white/10">
            <p className="text-sm text-slate-300 text-center">
              <span className="text-teal-300 font-medium">Your superpower:</span><br/>
              {topArchetype.superpower}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

// Your Vibe Check, plainly: that you took it, and where the results are. (It
// used to be a "Friendship Explorer Certificate" with a crown, "+200 XP
// Earned" and a share button.)
const VibeCheckCard = React.memo(function VibeCheckCard({ dominantArchetypes, setCurrentView }) {
  const primary = dominantArchetypes?.[0]?.details?.name;
  return (
    <Card className="bg-gradient-to-br from-green-500/10 to-teal-500/10 border-green-500/30">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-white text-lg">
          <ClipboardList className="h-5 w-5 text-green-400" />
          Your Vibe Check
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-slate-300">
          {primary
            ? `Your answers leaned most towards ${primary}. It's a snapshot of how you answered, and it can change.`
            : 'Your results are saved. They are a snapshot of how you answered, and they can change.'}
        </p>
        <Button
          size="sm"
          onClick={() => setCurrentView('vibe-assessment-results')}
          className="bg-teal-600 hover:bg-teal-700"
        >
          <Eye className="w-4 h-4 mr-2" />
          View your results
        </Button>
      </CardContent>
    </Card>
  );
});

const QuickActions = React.memo(function QuickActions({ setCurrentView, hasAssessment }) {
  return (
    <Card className="bg-white/5 backdrop-blur-sm border-white/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <Target className="h-5 w-5 text-coral-400" />
          Quick Actions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3">
          <Button
            onClick={() => setCurrentView('modules')}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 justify-start"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Learning Modules
            <ArrowRight className="w-4 h-4 ml-auto" />
          </Button>

          <Button
            onClick={() => setCurrentView('tools')}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 justify-start"
          >
            <Wrench className="w-5 h-5 mr-2" />
            Your Toolkit
            <ArrowRight className="w-4 h-4 ml-auto" />
          </Button>

          {!hasAssessment && (
            <Button
              onClick={() => setCurrentView('vibe-assessment-intro')}
              className="w-full bg-gradient-to-r from-teal-600 to-coral-600 hover:from-teal-700 hover:to-coral-700 justify-start"
            >
              <Brain className="w-5 h-5 mr-2" />
              Take Assessment
              <ArrowRight className="w-4 h-4 ml-auto" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

export default FriendshipVibeDashboard;
